import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Smartphone } from "lucide-react";
import { useApp } from "@/contexts/AppContext";

const DISMISS_KEY = "lo_install_dismissed_at";
const DISMISS_DAYS = 7;

export default function InstallPrompt() {
    const { lang } = useApp();
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // Already-installed detection
        const isStandalone =
            window.matchMedia?.("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (isStandalone) { setInstalled(true); return; }

        const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        const dismissedRecently = dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400_000;

        const onBefore = (e) => {
            e.preventDefault();
            setDeferred(e);
            if (!dismissedRecently) setTimeout(() => setVisible(true), 1200);
        };
        const onInstalled = () => { setInstalled(true); setVisible(false); setDeferred(null); };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const onInstall = async () => {
        if (!deferred) return;
        setBusy(true);
        try {
            deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice.outcome === "accepted") setInstalled(true);
            else localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } finally {
            setBusy(false);
            setVisible(false);
            setDeferred(null);
        }
    };

    const onDismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setVisible(false);
    };

    if (installed || !visible || !deferred) return null;

    const copy = {
        en: { title: "Install LO Portal", desc: "Add to your home screen for offline access & one-tap launch.", install: "Install", later: "Later" },
        od: { title: "LO ପୋର୍ଟାଲ୍ ସ୍ଥାପନ କରନ୍ତୁ", desc: "ଅଫଲାଇନ୍ ବ୍ୟବହାର ଓ ଏକ-ଟାପ୍ ସ୍ଟାର୍ଟ ପାଇଁ ହୋମ୍ ସ୍କ୍ରିନରେ ଯୋଡ଼ନ୍ତୁ ।", install: "ସ୍ଥାପନ", later: "ପରେ" },
    }[lang];

    return (
        <AnimatePresence>
            <motion.div
                key="install-prompt"
                data-testid="install-prompt"
                initial={{ y: 80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 80, opacity: 0 }}
                transition={{ type: "spring", stiffness: 360, damping: 30 }}
                className="fixed left-4 right-4 md:left-auto md:right-6 md:bottom-24 md:max-w-sm bottom-[92px] z-40"
            >
                <div className="glass-strong rounded-3xl border border-border/70 shadow-xl p-4 flex items-start gap-3">
                    <div className="h-11 w-11 shrink-0 rounded-2xl bg-gradient-to-br from-primary to-secondary grid place-items-center text-white shadow-sm">
                        <Smartphone className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className={`font-display font-bold text-sm leading-tight ${lang === "od" ? "font-odia" : ""}`}>{copy.title}</div>
                        <div className={`text-xs text-muted-foreground mt-0.5 leading-snug ${lang === "od" ? "font-odia" : ""}`}>{copy.desc}</div>
                        <div className="flex items-center gap-2 mt-3">
                            <button
                                data-testid="install-prompt-install"
                                onClick={onInstall}
                                disabled={busy}
                                className="rounded-full bg-primary text-primary-foreground text-xs font-semibold px-4 py-2 active:scale-95 transition-transform flex items-center gap-1.5 disabled:opacity-50"
                            >
                                <Download className="h-3.5 w-3.5" />
                                {copy.install}
                            </button>
                            <button
                                data-testid="install-prompt-later"
                                onClick={onDismiss}
                                className="rounded-full bg-muted text-xs font-semibold px-4 py-2 active:scale-95 transition-transform"
                            >
                                {copy.later}
                            </button>
                        </div>
                    </div>
                    <button
                        data-testid="install-prompt-close"
                        onClick={onDismiss}
                        className="h-7 w-7 grid place-items-center rounded-full hover:bg-muted transition-colors shrink-0"
                        aria-label="Close install prompt"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
